import { Listing, OfferType } from '@smg-real-estate/hg-rets-schema';
import { AgencyAgent, ListerBranding } from '@smg-real-estate/hg-search-libs';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { useRoute } from 'vue-router';

import {
  AgencyCardOutcome as ACO,
  getAgencyCardOutcome,
} from '@/helpers/AgencyCard/getAgencyCardOutcome';
import FeatureFlags from '@/helpers/FeatureToggle/FeatureFlags';
import { isFeatureEnabled } from '@/helpers/FeatureToggle/FeatureToggleHelper';
import { getPartnerBadge } from '@/helpers/partnerBadge/getPartnerBadge';

export interface UseAgencyCardOutcomeInput {
  listing: MaybeRefOrGetter<Listing | undefined>;
  listerBranding: MaybeRefOrGetter<ListerBranding | null | undefined>;
  agencyAgent: MaybeRefOrGetter<AgencyAgent | null | undefined>;
  legacyShouldShowAgencyCard: MaybeRefOrGetter<boolean>;
}

export function useAgencyCardOutcome({
  listing,
  listerBranding,
  agencyAgent,
  legacyShouldShowAgencyCard,
}: UseAgencyCardOutcomeInput) {
  const route = useRoute();
  const isPartnerBadgeEnabled = computed(() =>
    isFeatureEnabled(FeatureFlags.ENABLE_PARTNER_BADGE, route),
  );
  const outcome = computed(() =>
    getAgencyCardOutcome({
      isPremiumBranding: !!toValue(listerBranding)?.isPremiumBranding,
      hasAgencyAgent: !!toValue(agencyAgent),
      basePackage: toValue(listerBranding)?.basePackage,
      offerType: toValue(listing)?.offerType as OfferType | undefined,
    }),
  );
  const isOutcome = (...outcomes: ACO[]) => outcomes.includes(outcome.value);
  const shouldShowAgencyCard = computed(() => {
    if (!isPartnerBadgeEnabled.value) return toValue(legacyShouldShowAgencyCard);
    return isOutcome(ACO.AgencyCardWithPhoneWithoutBadge, ACO.AgencyCardWithoutPhoneWithBadge);
  });
  const shouldShowBrandedListingBox = computed(() => {
    if (!isPartnerBadgeEnabled.value) return !toValue(legacyShouldShowAgencyCard);
    return isOutcome(ACO.BrandedListingBoxWithBadge, ACO.BrandedListingBoxWithoutBadge);
  });
  const shouldShowPhoneNumber = computed(() => {
    if (!isPartnerBadgeEnabled.value) return true;
    return isOutcome(ACO.AgencyCardWithPhoneWithoutBadge);
  });
  const shouldShowPartnerBadge = computed(() => {
    if (!isPartnerBadgeEnabled.value) return false;
    return isOutcome(ACO.AgencyCardWithoutPhoneWithBadge, ACO.BrandedListingBoxWithBadge);
  });
  const partnerBadge = computed(() =>
    shouldShowPartnerBadge.value ? getPartnerBadge(toValue(listerBranding)?.basePackage) : null,
  );

  return {
    outcome,
    shouldShowAgencyCard,
    shouldShowBrandedListingBox,
    shouldShowPhoneNumber,
    shouldShowPartnerBadge,
    partnerBadge,
  };
}
